import styles from "./SuspendUserModal.module.css";
import PropTypes from "prop-types";
import { useState, useEffect } from "react";

import { Modal } from "@wwg/ui";
import Checkbox from "shared/@forms/Checkbox";

import { Select } from "shared/Select/Select";
import classNames from "classnames";
import emailTemplate from "../../Shared/EmailTemplate/emailTemplate";

/**
 *
 * Modal with a form to suspend a user.
 *
 */

export function SuspendUserModal({ userInfo, isOpen, onConfirm, onClose }) {
  const [dreamStream, setDreamStream] = useState(false);
  const [communikate, setCommunikate] = useState(false);
  const [events, setEvents] = useState(false);
  const [digitalDelivery, setDigitalDelivery] = useState(false);
  const [suspensionLength, setSuspensionLength] = useState("");
  const [suspendedIndefinitely, setSuspendedIndefinitely] = useState(false);
  const [suspensionReason, setSuspensionReason] = useState("");
  const [template, setTemplate] = useState("Choose Template");
  const [subject, setSubject] = useState("");
  const [email, setEmail] = useState("");
  const [reminder, setReminder] = useState("");
  const [errors, setErrors] = useState({
    platforms: "",
    reason: "",
    template: "",
    subject: "",
    email: "",
    suspensionLength: "",
    reminder: "",
  });

  useEffect(() => {
    if (template !== "Choose Template") {
      const getTemplate = emailTemplate(template);
      setSubject(getTemplate.subject);
      setEmail(getTemplate.body);
    }
  }, [template]);

  const handleSubmit = () => {
    let errors = {
      platforms: "",
      suspensionLength: "",
      reason: "",
      template: "",
      subject: "",
      email: "",
      reminder: "",
    };

    if (!dreamStream && !communikate && !events && !digitalDelivery) {
      errors.platforms = "Please select an option.";
    }

    if (suspensionLength === 0 && !suspendedIndefinitely) {
      errors.suspensionLength = "Suspension length cannot be set to 0.";
    }

    if (suspensionReason === "")
      errors.reason = "Please enter a suspension reason.";

    if (template === "") {
      errors.template = "Please select a template.";
    }

    if (subject === "") errors.subject = "Please enter a subject.";
    if (email === "") errors.email = "Please enter a body.";

    if (!reminder) errors.reminder = "Reminder days must be set.";

    let empty = true;
    for (let key in errors) {
      if (errors[key].length) empty = false;
    }
    if (empty) {
      const finalPlatforms = [];

      if (dreamStream) {
        finalPlatforms.push("DreamStream");
      }
      if (communikate) {
        finalPlatforms.push("CommuniKate");
      }
      if (events) {
        finalPlatforms.push("Events");
      }
      if (digitalDelivery) {
        finalPlatforms.push("Membership/Digital Delivery");
      }

      onConfirm({
        id: userInfo.case.incidentNumber,
        platforms: finalPlatforms,
        suspendIndefinitely: suspendedIndefinitely,
        suspendedDays: suspensionLength,
        reason: suspensionReason,
        reminderDays: reminder,
        template: template,
        subject: subject,
        emailBody: email,
      });
      onClose();
      clearForm();
    } else {
      setErrors({ ...errors });
    }
  };

  const clearForm = () => {
    setDreamStream(false);
    setCommunikate(false);
    setEvents(false);
    setDigitalDelivery(false);
    setSuspensionLength("");
    setSuspendedIndefinitely(false);
    setSuspensionReason("");
    setTemplate("");
    setSubject("");
    setEmail("");
    setReminder("");
    setErrors({
      platforms: "",
      reason: "",
      template: "",
      subject: "",
      email: "",
      suspensionLength: "",
      reminder: "",
    });
  };

  return (
    <Modal
      visible={isOpen}
      onClick={onClose}
      children={
        <div className={styles.container}>
          <div className={styles.header}>Suspend User</div>
          <div className={styles.hr} />
          <div className={styles.title}>
            What platforms is user being suspended from?
          </div>
          <Checkbox
            checked={dreamStream}
            onChange={(e) => {
              setDreamStream(!dreamStream);
            }}
            className={styles.checkbox}
          />
          <div className={styles.checkboxLabel}>DreamStream</div>
          <br />
          <Checkbox
            checked={communikate}
            onChange={(e) => {
              setCommunikate(!communikate);
            }}
            className={styles.checkbox}
          />
          <div className={styles.checkboxLabel}>CommuniKate</div>
          <br />
          <Checkbox
            checked={events}
            onChange={(e) => {
              setEvents(!events);
            }}
            className={styles.checkbox}
          />
          <div className={styles.checkboxLabel}>Events</div>
          <br />
          <Checkbox
            checked={digitalDelivery}
            onChange={(e) => {
              setDigitalDelivery(!digitalDelivery);
            }}
            className={styles.checkbox}
          />
          <div className={styles.checkboxLabel}>
            Membership/Digital Delivery
          </div>
          <br />
          <span className={styles.errors}>{errors.platforms}</span>
          <div className={styles.title}>
            How long will user be suspended for?
          </div>
          <div className={styles.durationSelect}>
            <Select
              id="suspensionLength"
              options={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]}
              placeholder={
                suspensionLength !== "" ? `${suspensionLength}` : "Select"
              }
              onChange={setSuspensionLength}
            />
          </div>
          <span>days</span> <span className={styles.or}>OR</span>
          <Checkbox
            id="suspendedIndefinitely"
            checked={suspendedIndefinitely}
            onChange={() => {
              setSuspendedIndefinitely(!suspendedIndefinitely);
              setSuspensionLength(0);
            }}
            className={styles.checkbox}
          />
          <div className={styles.checkboxLabel}>
            User will be suspended indefinitely
          </div>
          <span className={styles.errors}>{errors.suspensionLength}</span>
          <br />
          <div className={styles.title}>
            Why are you suspending this user?{" "}
            <span className={styles.keepItShort}>Keep it short.</span>
          </div>
          <input
            placeholder="Suspension Reason"
            id="suspensionReason"
            value={suspensionReason}
            onChange={(e) => {
              setSuspensionReason(e.target.value);
              setErrors({ ...errors, reason: "" });
            }}
            className={classNames(styles.input, styles.reasonInput)}
          />
          <span className={styles.errors}>{errors.reason}</span>
          <div className={styles.title}>Send an Email</div>
          <div className={styles.templateSelect}>
            <Select
              options={[
                "Caution Letter",
                "Review Letter",
                "Probation Letter",
                "Disciplinary Letter",
                "Disciplinary Letter - Physical Threat",
                "Withdrawl Letter",
                "Admonish Letter",
                "Reinstated Letter",
                "Case Completion Letter",
              ]}
              placeholder={template}
              onChange={setTemplate}
            />
          </div>
          <span className={styles.errors}>{errors.template}</span>
          <input
            placeholder="Subject Line"
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              setErrors({ ...errors, subject: "" });
            }}
            className={classNames(styles.input)}
          />
          <span className={styles.errors}>{errors.subject}</span>
          <textarea
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors({ ...errors, email: "" });
            }}
            placeholder="Add text here…"
            className={classNames(styles.textarea)}
            value={email}
          ></textarea>
          <span className={styles.errors}>{errors.email}</span>
          <div className={styles.title}>Set Reminder</div>
          <div>
            <span className={styles.remindMeIn}>Remind me in </span>
            <span className={styles.durationSelect}>
              <Select
                id="reminder"
                options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]}
                placeholder={reminder !== "" ? `${reminder}` : "Select"}
                onChange={setReminder}
              />
            </span>
            <span> days.</span>
            <br></br>
            <span className={styles.errors}>{errors.reminder}</span>
          </div>
          <div className={styles.buttonContainer}>
            <button className={styles.button} onClick={() => handleSubmit()}>
              Send for Approval
            </button>
          </div>
        </div>
      }
    />
  );
}

SuspendUserModal.propTypes = {
  userInfo: PropTypes.object,
  isOpen: PropTypes.bool,
  onConfirm: PropTypes.func,
  onClose: PropTypes.func,
};
